import { useState, useEffect } from "react";
import { Plus, Trash2, Wallet, TrendingUp, Receipt, Scale, CheckCircle2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { addPayment, deletePayment, updateCase } from "@/lib/api";
import { formatTRY, formatDate } from "@/lib/constants";
import InstallmentPlan from "@/components/InstallmentPlan";

export default function FinancialPanel({ caseData, payments, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", payment_date: "", note: "", tip: "pesin" });
  // Karşı vekalet local state
  const [kvAmount, setKvAmount] = useState(
    caseData.karsi_vekalet_tutari != null ? String(caseData.karsi_vekalet_tutari) : ""
  );
  const [kvDone, setKvDone] = useState(Boolean(caseData.karsi_vekalet_tamamlandi));
  const [kvSaving, setKvSaving] = useState(false);
  // Resync when caseData changes (different case selected or saved)
  useEffect(() => {
    setKvAmount(caseData.karsi_vekalet_tutari != null ? String(caseData.karsi_vekalet_tutari) : "");
    setKvDone(Boolean(caseData.karsi_vekalet_tamamlandi));
  }, [caseData.id, caseData.karsi_vekalet_tutari, caseData.karsi_vekalet_tamamlandi]);
  const kvDirty = (parseFloat(kvAmount || 0) || 0) !== (caseData.karsi_vekalet_tutari || 0)
    || kvDone !== Boolean(caseData.karsi_vekalet_tamamlandi);

  const total = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const balance = (caseData.is_bedeli || 0) - total;
  const pct = caseData.is_bedeli ? Math.min(100, Math.round((total / caseData.is_bedeli) * 100)) : 0;

  const submit = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Geçerli bir tutar girin"); return; }
    try {
      await addPayment(caseData.id, {
        amount: amt,
        payment_date: form.payment_date || null,
        note: form.note,
        tip: form.tip,
      });
      toast.success("Tahsilat eklendi");
      setForm({ amount: "", payment_date: "", note: "", tip: "pesin" });
      setOpen(false);
      onRefresh?.();
    } catch { toast.error("Tahsilat eklenemedi"); }
  };

  const remove = async (id) => {
    try { await deletePayment(id); toast.success("Tahsilat silindi"); onRefresh?.(); }
    catch { toast.error("Silinemedi"); }
  };

  const saveKv = async () => {
    setKvSaving(true);
    try {
      await updateCase(caseData.id, {
        karsi_vekalet_tutari: parseFloat(kvAmount || 0) || 0,
        karsi_vekalet_tamamlandi: kvDone,
      });
      toast.success("Karşı vekalet ücreti güncellendi");
      onRefresh?.();
    } catch { toast.error("Kaydedilemedi"); }
    finally { setKvSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="financial-panel">
      {/* Two-column: Anlaşma Bedeli + Karşı Vekalet Ücret Tutarı */}
      <div className="grid lg:grid-cols-2 gap-5">
        {/* Anlaşma Bedeli (previously Vekalet Ücreti & Tahsilat Yönetimi) */}
        <Card className="bg-slate-900/70 border-slate-800" data-testid="anlasma-bedeli-card">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-amber-500" />
              <div className="font-serif text-xl text-slate-100">Anlaşma Bedeli</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MiniStat label="Toplam İş Bedeli" value={formatTRY(caseData.is_bedeli)} />
              <MiniStat label="Tahsil Edilen" value={formatTRY(total)} valueClass="text-emerald-400" />
              <MiniStat label="Kalan Bakiye" value={formatTRY(balance)} valueClass="text-red-400" />
              <MiniStat label="Tahsilat Durumu" value={`%${pct}`} valueClass="text-amber-500" />
            </div>
            <div>
              <Progress value={pct} className="h-2 bg-slate-800" />
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mt-1">
                Ödeme Tipi: {caseData.odeme_tipi === "taksit" ? `Taksitli (${caseData.taksit_sayisi} taksit)` : "Peşin"}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Karşı Vekalet Ücret Tutarı */}
        <Card className="bg-slate-900/70 border-slate-800" data-testid="karsi-vekalet-card">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Scale className="w-5 h-5 text-amber-500" />
                <div>
                  <div className="font-serif text-xl text-slate-100">Karşı Vekalet Ücret Tutarı</div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">
                    Karşı taraftan tahsil edilecek vekalet
                  </div>
                </div>
              </div>
              {kvDone ? (
                <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Tamamlandı
                </Badge>
              ) : (
                <Badge variant="outline" className="border-amber-700/60 text-amber-400">Tamamlanmadı</Badge>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-400">Tutar (₺)</Label>
              <Input
                type="number" min="0" step="0.01"
                value={kvAmount}
                onChange={(e) => setKvAmount(e.target.value)}
                placeholder="0,00"
                className="bg-slate-800 border-slate-700 mt-1 text-slate-100 font-mono"
                data-testid="karsi-vekalet-amount-input"
              />
              <div className="text-2xl font-serif text-amber-500 mt-2 font-mono">
                {formatTRY(parseFloat(kvAmount || 0) || 0)}
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <Checkbox
                checked={kvDone}
                onCheckedChange={(v) => setKvDone(Boolean(v))}
                className="border-slate-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                data-testid="karsi-vekalet-done-checkbox"
              />
              <span className="text-sm text-slate-200">Tahsilat Tamamlandı</span>
            </label>
            <Button size="sm" onClick={saveKv} disabled={!kvDirty || kvSaving}
              className="bg-amber-600 hover:bg-amber-700 text-slate-950 w-full disabled:opacity-40"
              data-testid="karsi-vekalet-save-btn">
              <Save className="w-3.5 h-3.5 mr-1" />
              {kvSaving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </CardContent>
        </Card>
      </div>

      <InstallmentPlan caseId={caseData.id} refreshKey={payments.length} />

      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-300 font-medium">Tahsilat Kayıtları ({payments.length})</div>
        <Button onClick={() => setOpen(true)} size="sm" className="bg-amber-600 hover:bg-amber-700 text-slate-950" data-testid="add-payment-btn">
          <Plus className="w-4 h-4 mr-1" /> Tahsilat Ekle
        </Button>
      </div>

      {payments.length === 0 ? (
        <div className="text-xs text-slate-500 italic px-3 py-8 text-center border border-dashed border-slate-800 rounded">
          Henüz tahsilat kaydı yok
        </div>
      ) : (
        <div className="border border-slate-800 rounded overflow-hidden" data-testid="payments-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/80 text-slate-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">Tarih</th>
                <th className="px-4 py-2 text-left">Tip</th>
                <th className="px-4 py-2 text-left">Açıklama</th>
                <th className="px-4 py-2 text-right">Tutar</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {payments.map((p) => (
                <tr key={p.id} className="hover:bg-slate-900/40" data-testid={`payment-row-${p.id}`}>
                  <td className="px-4 py-2 font-mono text-slate-300">{formatDate(p.payment_date)}</td>
                  <td className="px-4 py-2 text-slate-400">{p.tip === "taksit" ? "Taksit" : "Peşin"}</td>
                  <td className="px-4 py-2 text-slate-300">{p.note || "—"}</td>
                  <td className="px-4 py-2 text-right font-mono text-emerald-400">{formatTRY(p.amount)}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => remove(p.id)} className="text-slate-500 hover:text-red-400" data-testid={`payment-delete-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="payment-dialog">
          <DialogHeader><DialogTitle className="font-serif text-amber-500">Tahsilat Ekle</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-400">Tutar (₺) *</Label>
              <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="bg-slate-800 border-slate-700 mt-1 font-mono" data-testid="input-payment-amount" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Tarih</Label>
              <Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                className="bg-slate-800 border-slate-700 mt-1" data-testid="input-payment-date" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-400">Tip</Label>
              <Select value={form.tip} onValueChange={(v) => setForm({ ...form, tip: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="pesin">Peşin</SelectItem>
                  <SelectItem value="taksit">Taksit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-400">Açıklama</Label>
              <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="bg-slate-800 border-slate-700 mt-1" data-testid="input-payment-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={submit} className="bg-amber-600 hover:bg-amber-700 text-slate-950" data-testid="payment-save-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniStat({ label, value, valueClass = "text-slate-100" }) {
  return (
    <div className="rounded border border-slate-800 bg-slate-950/40 px-3 py-2">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 text-base font-serif font-semibold ${valueClass}`}>{value}</div>
    </div>
  );
}

function StatCard({ label, value, valueClass = "text-slate-100", icon }) {
  return (
    <Card className="bg-slate-900/70 border-slate-800">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">
          {icon} {label}
        </div>
        <div className={`mt-2 text-xl font-serif font-semibold ${valueClass}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
